import * as React from "react"
import * as AccordionPrimitive from "@radix-ui/react-accordion"
import { ChevronDown } from "lucide-react"

import { cn } from "@/lib/utils"
import styles from "./accordion.module.css"

const Accordion = AccordionPrimitive.Root

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    className={cn(styles.item, className)}
    {...props}
  />
))
AccordionItem.displayName = "AccordionItem"

const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Header className={styles.header}>
    <AccordionPrimitive.Trigger
      ref={ref}
      className={cn(styles.trigger, className)}
      {...props}
    >
      {children}
      <ChevronDown className={styles.chevron} aria-hidden />
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
))
AccordionTrigger.displayName = "AccordionTrigger"

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  // Deliberately NO `forceMount`. Radix's own `Presence` already keeps the
  // node mounted until the exit animation finishes, so it isn't needed — and
  // it actively breaks the OPEN animation:
  //
  // Radix measures the panel with `getBoundingClientRect()` and publishes it
  // as `--radix-collapsible-content-height`, but only via `height ? … : void 0`
  // where `height` comes from a REF read at render time. With `forceMount` the
  // closed panel is still in the DOM and our CSS collapses it to `height: 0`,
  // so Radix measures 0 — falsy — and the variable is left undefined. The
  // `accordion-down` keyframe's `to { height: var(…) }` is then invalid, the
  // height is dropped from the keyframe, and the panel snaps open while only
  // opacity animates. Close looked fine because its height was measured while
  // the panel was open.
  //
  // `forceMount` was originally added because close appeared to fail. The real
  // cause was that the keyframes lived in a global stylesheet while the
  // `animation:` reference sat in a CSS Module, so the scoped name matched no
  // keyframe and no animation existed at all — Presence saw nothing to wait
  // for and unmounted immediately. That's fixed in accordion.module.css.
  <AccordionPrimitive.Content
    ref={ref}
    className={cn(styles.content, className)}
    {...props}
  >
    <div className={styles.contentInner}>{children}</div>
  </AccordionPrimitive.Content>
))
AccordionContent.displayName = "AccordionContent"

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent }
